/**
 * MoCRA (Modernization of Cosmetics Regulation Act of 2022) evaluator.
 *
 * Implements the full set of MoCRA requirements and produces structured
 * issue records consumable by the compliance pipeline.
 *
 * Scope covered:
 *   - Facility registration with FDA (21 USC § 364a)
 *   - Product listing with FDA (21 USC § 364a)
 *   - Responsible Person identification (21 USC § 364c(b))
 *   - Adverse event reporting (21 USC § 364c)
 *   - Safety substantiation records (21 USC § 364d)
 *   - Good Manufacturing Practices (21 USC § 364b)
 *   - PFAS screening and reporting
 *   - Talc / asbestos testing (21 CFR § 700.30 proposed)
 *   - Fragrance allergen disclosure (FDA proposed rule June 2024)
 *   - Professional use warning (21 USC § 364e(b))
 *   - Small business exemption logic
 */

import mocraReference from "../data/mocra_reference.json";

const normalizeText = (value) =>
  String(value || "").toLowerCase().replace(/\s+/g, " ").trim();

const normalizeCas = (value) =>
  normalizeText(value).replace(/\s+/g, "").replace(/^n\/a$/, "");

const BUSINESS_DAYS_MS = 24 * 60 * 60 * 1000;

const isSmallBusiness = (product, responsibleBusiness = {}) => {
  if (responsibleBusiness.smallBusinessAttested !== true) return false;
  const avg = Number(responsibleBusiness.averageAnnualRevenueUsd);
  if (!Number.isFinite(avg)) return false;
  if (avg >= mocraReference.smallBusinessThreshold.annualGrossSalesUsd) return false;
  // Categories that are excluded from small-business exemption even below threshold
  const category = normalizeText(product?.category || "");
  const fn = normalizeText(product?.function || "");
  const hay = `${category} ${fn}`;
  return !mocraReference.smallBusinessThreshold.excludedCategories.some((ex) =>
    hay.includes(normalizeText(ex))
  );
};

const createIssue = ({
  code,
  severity = "error",
  scope = "formula",
  message,
  source = mocraReference.metadata.title,
  sourceUrl = mocraReference.metadata.sourceUrl,
  sourceQuote = "",
  sourceQuotes = null,
  highlight = null,
  blocking = true,
}) => {
  const issue = {
    code,
    severity,
    jurisdiction: "US",
    scope,
    message,
    source,
    sourceUrl,
    sourceQuote,
    blocking,
  };
  if (Array.isArray(sourceQuotes) && sourceQuotes.length) {
    issue.sourceQuotes = sourceQuotes.filter((entry) => entry && (entry.quote || entry.text));
  }
  if (highlight) {
    issue.highlight = highlight;
  }
  return issue;
};

// ---------------------------------------------------------------------------
// Shared MoCRA quote fragments
// ---------------------------------------------------------------------------
const MOCRA_OVERVIEW_QUOTE = {
  label: "MoCRA · Overview",
  source: "FDA · MoCRA 2022 overview",
  sourceUrl: mocraReference.fdaSources?.cosmeticsDirect?.url || mocraReference.metadata.sourceUrl,
  quote:
    "The Modernization of Cosmetics Regulation Act of 2022 (MoCRA) expanded FDA authority over cosmetic products, including facility registration, product listing, adverse event reporting, safety substantiation, Good Manufacturing Practices, and mandatory recall authority.",
};

const MOCRA_FACILITY_REGISTRATION_QUOTE = {
  label: "§ 607 · Facility registration",
  source: "21 USC § 364a(a)",
  sourceUrl: mocraReference.fdaSources?.cosmeticsDirect?.url || mocraReference.metadata.sourceUrl,
  quote:
    "Each person that owns or operates a facility that engages in the manufacturing or processing of a cosmetic product for distribution in the United States shall register each such facility with the Secretary, and renew such registration biennially.",
};

const MOCRA_PRODUCT_LISTING_QUOTE = {
  label: "§ 607 · Product listing",
  source: "21 USC § 364a(c)",
  sourceUrl: mocraReference.fdaSources?.cosmeticsDirect?.url || mocraReference.metadata.sourceUrl,
  quote:
    "Each responsible person shall submit to the Secretary a cosmetic product listing for each cosmetic product, including product ingredients. Listing submissions shall be updated annually.",
};

const MOCRA_SAFETY_SUBSTANTIATION_QUOTE = {
  label: "§ 608 · Safety substantiation",
  source: "21 USC § 364d",
  sourceUrl: mocraReference.metadata.sourceUrl,
  quote:
    "The responsible person shall ensure, and maintain records supporting, that there is adequate substantiation of safety of such cosmetic product. Records shall be made available to the Secretary on request.",
};

const MOCRA_ADVERSE_EVENT_QUOTE = {
  label: "§ 605 · Adverse event reporting",
  source: "21 USC § 364c",
  sourceUrl: mocraReference.fdaSources?.caers?.url || mocraReference.metadata.sourceUrl,
  quote:
    "Not later than 15 business days after the responsible person receives a report of a serious adverse event, the responsible person shall submit such report to the Secretary. Records shall be maintained for six years.",
};

const MOCRA_GMP_QUOTE = {
  label: "§ 606 · Good Manufacturing Practices",
  source: "21 USC § 364b",
  sourceUrl: mocraReference.metadata.sourceUrl,
  quote:
    "The Secretary shall promulgate regulations to establish good manufacturing practice requirements for facilities consistent, to the extent practicable, with recognised cosmetic GMP standards. Facilities are expected to attest to ongoing compliance.",
};

const MOCRA_PFAS_QUOTE = {
  label: "MoCRA · PFAS reporting",
  source: "FD&C Act § 608",
  sourceUrl: mocraReference.fdaSources?.pfas?.url || mocraReference.metadata.sourceUrl,
  quote:
    "The Secretary shall assess the use of PFAS in cosmetic products and the scientific evidence regarding the safety of such use. Manufacturers must identify any intentionally added PFAS and their purpose / concentration.",
};

const MOCRA_TALC_QUOTE = {
  label: "§ 3505 · Talc asbestos testing",
  source: "21 CFR § 700.30 (proposed)",
  sourceUrl: "https://www.fda.gov/cosmetics/cosmetic-ingredients/talc",
  quote:
    "The Secretary shall promulgate regulations to establish and require standardised testing methods for detecting and identifying asbestos in talc-containing cosmetic products. Acceptable methods include polarised light microscopy (PLM) and transmission electron microscopy (TEM/EDS/SAED).",
};

const MOCRA_PROFESSIONAL_USE_QUOTE = {
  label: "MoCRA · Professional use labeling",
  source: "21 USC § 364e(b)",
  sourceUrl: mocraReference.metadata.sourceUrl,
  quote:
    "A cosmetic product introduced into interstate commerce that is intended for professional use shall include a clear and prominent statement identifying the product as for professional use, and shall identify the responsible person.",
};

const MOCRA_FRAGRANCE_ALLERGEN_QUOTE = {
  label: "FDA proposed rule · Fragrance allergen disclosure",
  source: "89 FR 53494 (June 25, 2024)",
  sourceUrl: mocraReference.fragranceAllergenDisclosure?.sourceUrl || mocraReference.metadata.sourceUrl,
  quote:
    "FDA is proposing to require disclosure of fragrance allergens on cosmetic product labels when present above specified concentration thresholds, broadly aligned with the allergen list maintained by the European Union under Regulation (EC) 1223/2009.",
};

// ---------------------------------------------------------------------------
// PFAS screening
// ---------------------------------------------------------------------------

const PFAS_CAS_SET = new Set(
  mocraReference.pfasSubstances.knownCas.map((p) => normalizeCas(p.cas))
);

const detectPfasIngredient = (ingredient) => {
  const casList = String(ingredient.cas_number || "")
    .split(",")
    .map(normalizeCas)
    .filter(Boolean);
  const hitByCas = casList.find((c) => PFAS_CAS_SET.has(c));
  if (hitByCas) {
    const match = mocraReference.pfasSubstances.knownCas.find(
      (p) => normalizeCas(p.cas) === hitByCas
    );
    return { matched: true, cas: hitByCas, name: match?.name || ingredient.ingredient_name };
  }
  const name = normalizeText(ingredient.ingredient_name);
  const pattern = mocraReference.pfasSubstances.namePatterns.find((p) =>
    name.includes(normalizeText(p))
  );
  if (pattern) {
    return { matched: true, cas: "", name: ingredient.ingredient_name, pattern };
  }
  return { matched: false };
};

// ---------------------------------------------------------------------------
// Talc screening
// ---------------------------------------------------------------------------

const TALC_CAS_SET = new Set(
  mocraReference.talcSubstances.knownCas.map((t) => normalizeCas(t.cas))
);

const detectTalcIngredient = (ingredient) => {
  const casList = String(ingredient.cas_number || "")
    .split(",")
    .map(normalizeCas)
    .filter(Boolean);
  if (casList.some((c) => TALC_CAS_SET.has(c))) {
    return { matched: true, cas: casList.find((c) => TALC_CAS_SET.has(c)) };
  }
  const name = normalizeText(ingredient.ingredient_name);
  if (mocraReference.talcSubstances.namePatterns.some((p) => name.includes(normalizeText(p)))) {
    return { matched: true, cas: "" };
  }
  return { matched: false };
};

// ---------------------------------------------------------------------------
// Professional use detection
// ---------------------------------------------------------------------------

const isProfessionalUseProduct = (product) => {
  const hay = normalizeText(
    [product.function, product.category, ...(product.claims || [])].filter(Boolean).join(" ")
  );
  return mocraReference.professionalUseWarning.triggers.some((t) => hay.includes(normalizeText(t)));
};

// ---------------------------------------------------------------------------
// Adverse event checks
// ---------------------------------------------------------------------------

const hasUnresolvedSeriousAdverseEvents = (product) => {
  const events = product.adverseEvents || [];
  return events.some((e) => e.serious === true && e.status !== "Reported to FDA");
};

const hasOverdueReportedEvents = (product, now = new Date()) => {
  const events = product.adverseEvents || [];
  return events.some((e) => {
    if (!e.serious) return false;
    if (e.status === "Reported to FDA") return false;
    if (!e.occurredAt) return false;
    const occurred = new Date(e.occurredAt);
    if (Number.isNaN(occurred.getTime())) return false;
    const daysElapsed = (now.getTime() - occurred.getTime()) / BUSINESS_DAYS_MS;
    return daysElapsed > mocraReference.adverseEventReporting.seriousWithinBusinessDays;
  });
};

// ---------------------------------------------------------------------------
// Vendor / facility registration checks
// ---------------------------------------------------------------------------

const unregisteredFacilities = (product, vendors = []) => {
  const productVendors = (product.vendorIds || [])
    .map((id) => vendors.find((v) => v.id === id))
    .filter(Boolean);
  return productVendors.filter((vendor) => {
    const facility = vendor.facility || {};
    const registered = Boolean(facility.fdaFacilityRegistered);
    const expired =
      facility.fdaRegistrationExpiresAt &&
      new Date(facility.fdaRegistrationExpiresAt).getTime() < Date.now();
    return !registered || expired;
  });
};

// ---------------------------------------------------------------------------
// Main MoCRA evaluator
// ---------------------------------------------------------------------------

export const evaluateMocraCompliance = ({
  product = {},
  ifraResults = [],
  documents = [],
  vendors = [],
  euAllergenDetails = [],
} = {}) => {
  const issues = [];
  const facts = {};

  const mocra = product.mocra || {};
  const responsibleBusiness = mocra.responsibleBusiness || {};
  const smallBiz = isSmallBusiness(product, responsibleBusiness);
  facts.smallBusiness = smallBiz;
  facts.smallBusinessThreshold = mocraReference.smallBusinessThreshold.annualGrossSalesUsd;

  const productHighlight = [product.name, product.brand].filter(Boolean);

  // ── 1. Responsible Person on label ────────────────────────────────────────
  if (!product.responsiblePerson) {
    issues.push(
      createIssue({
        code: "US_MOCRA_RESPONSIBLE_PERSON_MISSING",
        scope: "label",
        severity: "critical",
        blocking: true,
        message: "Responsible Person name and contact missing; required on label for adverse event reporting.",
        source: "MoCRA § 607 / 21 USC § 364c(b)",
        sourceUrl: "https://www.fda.gov/cosmetics/cosmetics-laws-regulations/modernization-cosmetics-regulation-act-2022-mocra",
        sourceQuotes: [
          {
            label: `${product.name || "This product"} · label contact`,
            source: "MoCRA § 607 / 21 USC § 364c(b)",
            sourceUrl: "https://www.fda.gov/cosmetics/cosmetics-laws-regulations/modernization-cosmetics-regulation-act-2022-mocra",
            quote: `Each cosmetic product shall bear a label or labeling that provides a domestic address, domestic phone number, or electronic contact information through which the responsible person can receive adverse event reports. ${product.name || "This product"} has no responsible person configured.`,
          },
          MOCRA_ADVERSE_EVENT_QUOTE,
          MOCRA_OVERVIEW_QUOTE,
        ],
        highlight: productHighlight,
      })
    );
  }

  // ── 2. Facility Registration ──────────────────────────────────────────────
  if (!smallBiz) {
    const missingVendors = unregisteredFacilities(product, vendors);
    facts.unregisteredFacilityCount = missingVendors.length;
    if (missingVendors.length > 0) {
      const vendorNames = missingVendors.map((v) => v.name).filter(Boolean);
      issues.push(
        createIssue({
          code: "US_MOCRA_FACILITY_UNREGISTERED",
          scope: "facility",
          severity: "critical",
          blocking: true,
          message: `${missingVendors.length} manufacturing ${missingVendors.length === 1 ? "facility is" : "facilities are"} not registered with FDA (or registration expired).`,
          source: "MoCRA § 607 / 21 USC § 364a(a)",
          sourceUrl: mocraReference.fdaSources.cosmeticsDirect.url,
          sourceQuotes: [
            {
              label: `Unregistered facilities · ${missingVendors.length}`,
              source: "Vendor registry",
              sourceUrl: mocraReference.fdaSources.cosmeticsDirect.url,
              quote: `The following ${missingVendors.length === 1 ? "facility is" : "facilities are"} associated with this product but lack a current FDA facility registration: ${vendorNames.join(", ") || "(unnamed)"}.`,
            },
            MOCRA_FACILITY_REGISTRATION_QUOTE,
          ],
          highlight: vendorNames,
        })
      );
    }
  }

  // ── 3. Product Listing ────────────────────────────────────────────────────
  const listing = mocra.fdaListing || {};
  facts.listingNumber = listing.listingNumber || "";
  facts.listingLastUpdatedAt = listing.lastUpdatedAt || "";
  if (!smallBiz) {
    if (!listing.listingNumber) {
      issues.push(
        createIssue({
          code: "US_MOCRA_PRODUCT_NOT_LISTED",
          scope: "listing",
          severity: "critical",
          blocking: true,
          message: "Product is not listed with FDA. MoCRA requires listing via Cosmetics Direct.",
          source: "MoCRA § 607 / 21 USC § 364a(c)",
          sourceUrl: mocraReference.fdaSources.cosmeticsDirect.url,
          sourceQuotes: [
            {
              label: `${product.name || "This product"} · listing status`,
              source: "Cosmetics Direct submission",
              sourceUrl: mocraReference.fdaSources.cosmeticsDirect.url,
              quote: `${product.name || "This product"} does not have a Cosmetics Direct listing number on file. Listing submission is required before distribution in the United States.`,
            },
            MOCRA_PRODUCT_LISTING_QUOTE,
          ],
          highlight: productHighlight,
        })
      );
    } else {
      const lastUpdated = listing.lastUpdatedAt ? new Date(listing.lastUpdatedAt) : null;
      if (lastUpdated && !Number.isNaN(lastUpdated.getTime())) {
        const days = (Date.now() - lastUpdated.getTime()) / (24 * 60 * 60 * 1000);
        if (days > 365) {
          issues.push(
            createIssue({
              code: "US_MOCRA_PRODUCT_LISTING_STALE",
              scope: "listing",
              severity: "warning",
              blocking: false,
              message: `Product listing last updated ${Math.round(days)} days ago; annual update required.`,
              source: "MoCRA § 607 / 21 USC § 364a(c)(4)",
              sourceUrl: mocraReference.fdaSources.cosmeticsDirect.url,
              sourceQuotes: [
                {
                  label: `${product.name || "This product"} · last listing update`,
                  source: "Cosmetics Direct",
                  sourceUrl: mocraReference.fdaSources.cosmeticsDirect.url,
                  quote: `Listing ${listing.listingNumber} for ${product.name || "this product"} was last updated ${Math.round(days)} days ago. Annual refresh is required to keep the listing current.`,
                },
                MOCRA_PRODUCT_LISTING_QUOTE,
              ],
              highlight: [listing.listingNumber, ...productHighlight].filter(Boolean),
            })
          );
        }
      }
    }
  }

  // ── 4. Safety Substantiation ──────────────────────────────────────────────
  const substantiation = mocra.safetySubstantiation || {};
  facts.hasSafetySubstantiation = Boolean(substantiation.onFile);
  if (!substantiation.onFile) {
    issues.push(
      createIssue({
        code: "US_MOCRA_SAFETY_SUBSTANTIATION_MISSING",
        scope: "safety",
        severity: "critical",
        blocking: true,
        message: "Safety substantiation records not on file. Required for all MoCRA products including small businesses.",
        source: "MoCRA § 608 / 21 USC § 364d",
        sourceUrl: "https://www.fda.gov/cosmetics/cosmetics-laws-regulations/modernization-cosmetics-regulation-act-2022-mocra",
        sourceQuotes: [
          {
            label: `${product.name || "This product"} · safety file`,
            source: "Internal records",
            sourceUrl: "",
            quote: `${product.name || "This product"} has no safety substantiation record on file. MoCRA requires ongoing maintenance of evidence supporting product safety, available to FDA on request.`,
          },
          MOCRA_SAFETY_SUBSTANTIATION_QUOTE,
        ],
        highlight: productHighlight,
      })
    );
  }

  // ── 5. Adverse Event Reporting ────────────────────────────────────────────
  const events = product.adverseEvents || [];
  facts.adverseEventCount = events.length;
  facts.seriousAdverseEventCount = events.filter((e) => e.serious).length;
  facts.unresolvedSeriousAdverseEvents = hasUnresolvedSeriousAdverseEvents(product);

  if (hasOverdueReportedEvents(product)) {
    const overdueEvents = events.filter((e) => {
      if (!e.serious || e.status === "Reported to FDA" || !e.occurredAt) return false;
      const occurred = new Date(e.occurredAt);
      if (Number.isNaN(occurred.getTime())) return false;
      return (Date.now() - occurred.getTime()) / BUSINESS_DAYS_MS > 15;
    });
    issues.push(
      createIssue({
        code: "US_MOCRA_ADVERSE_EVENT_OVERDUE",
        scope: "pharmacovigilance",
        severity: "critical",
        blocking: true,
        message: "Serious adverse event not reported to FDA within 15 business days of receipt.",
        source: "MoCRA § 605 / 21 USC § 364c(b)",
        sourceUrl: mocraReference.fdaSources.caers.url,
        sourceQuotes: [
          {
            label: `Overdue events · ${overdueEvents.length}`,
            source: "Internal pharmacovigilance log",
            sourceUrl: mocraReference.fdaSources.caers.url,
            quote: `${overdueEvents.length} serious adverse event${overdueEvents.length === 1 ? "" : "s"} for ${product.name || "this product"} ${overdueEvents.length === 1 ? "is" : "are"} past the 15-business-day FDA submission window.`,
          },
          MOCRA_ADVERSE_EVENT_QUOTE,
        ],
        highlight: productHighlight,
      })
    );
  } else if (facts.unresolvedSeriousAdverseEvents) {
    issues.push(
      createIssue({
        code: "US_MOCRA_ADVERSE_EVENT_UNRESOLVED",
        scope: "pharmacovigilance",
        severity: "error",
        blocking: true,
        message: `${facts.seriousAdverseEventCount} serious adverse event${facts.seriousAdverseEventCount === 1 ? "" : "s"} pending FDA submission.`,
        source: "MoCRA § 605",
        sourceUrl: mocraReference.fdaSources.caers.url,
        sourceQuotes: [
          {
            label: `Unresolved events · ${facts.seriousAdverseEventCount}`,
            source: "Internal pharmacovigilance log",
            sourceUrl: mocraReference.fdaSources.caers.url,
            quote: `${facts.seriousAdverseEventCount} serious adverse event${facts.seriousAdverseEventCount === 1 ? "" : "s"} for ${product.name || "this product"} ${facts.seriousAdverseEventCount === 1 ? "is" : "are"} logged but not yet submitted to FDA.`,
          },
          MOCRA_ADVERSE_EVENT_QUOTE,
        ],
        highlight: productHighlight,
      })
    );
  }

  // ── 6. PFAS screening ─────────────────────────────────────────────────────
  const pfasHits = ifraResults
    .map((ingredient) => ({ ingredient, detection: detectPfasIngredient(ingredient) }))
    .filter((x) => x.detection.matched);
  facts.pfasHitCount = pfasHits.length;
  facts.pfasIngredients = pfasHits.map((x) => x.ingredient.ingredient_name);
  if (pfasHits.length > 0) {
    const disclosed = Boolean(mocra.pfas?.disclosed);
    const pfasNames = pfasHits.map((x) => x.ingredient.ingredient_name);
    const pfasCases = pfasHits.map((x) => x.detection.cas).filter(Boolean);
    issues.push(
      createIssue({
        code: disclosed ? "US_MOCRA_PFAS_DISCLOSED" : "US_MOCRA_PFAS_UNDISCLOSED",
        scope: "ingredient",
        severity: disclosed ? "warning" : "error",
        blocking: !disclosed,
        message: disclosed
          ? `${pfasHits.length} PFAS ingredient${pfasHits.length === 1 ? "" : "s"} present and disclosed: ${pfasNames.join(", ")}.`
          : `${pfasHits.length} PFAS ingredient${pfasHits.length === 1 ? "" : "s"} detected but not disclosed: ${pfasNames.join(", ")}.`,
        source: "MoCRA PFAS reporting / FD&C Act § 608",
        sourceUrl: mocraReference.fdaSources.pfas.url,
        sourceQuotes: [
          {
            label: `PFAS detected · ${pfasHits.length}`,
            source: "Formula screen vs EPA CompTox PFASSTRUCT",
            sourceUrl: mocraReference.pfasSubstances.sourceUrl,
            quote: disclosed
              ? `${pfasNames.join(", ")} ${pfasHits.length === 1 ? "is" : "are"} present in ${product.name || "this product"} and ${pfasHits.length === 1 ? "is" : "are"} already disclosed in the product's PFAS declarations.`
              : `${pfasNames.join(", ")} ${pfasHits.length === 1 ? "is a" : "are"} PFAS ingredient${pfasHits.length === 1 ? "" : "s"} detected in ${product.name || "this product"}, but the product record lists no PFAS disclosure.${pfasCases.length ? ` Matched CAS: ${pfasCases.join(", ")}.` : ""}`,
          },
          MOCRA_PFAS_QUOTE,
        ],
        highlight: [...pfasNames, ...pfasCases],
      })
    );
  }

  // ── 7. Talc / asbestos testing ────────────────────────────────────────────
  const talcHits = ifraResults
    .map((ingredient) => ({ ingredient, detection: detectTalcIngredient(ingredient) }))
    .filter((x) => x.detection.matched);
  facts.containsTalc = talcHits.length > 0;
  if (talcHits.length > 0) {
    const testing = mocra.talcTesting || {};
    const hasValidTest = Boolean(testing.asbestosTestedAt) && testing.result === "No asbestos detected";
    if (!hasValidTest) {
      const talcNames = talcHits.map((x) => x.ingredient.ingredient_name);
      issues.push(
        createIssue({
          code: "US_MOCRA_TALC_ASBESTOS_TESTING_MISSING",
          scope: "ingredient",
          severity: "critical",
          blocking: true,
          message: `Product contains talc but no valid asbestos testing (PLM/TEM) on file.`,
          source: "21 CFR § 700.30 (proposed) / MoCRA § 3505",
          sourceUrl: "https://www.fda.gov/cosmetics/cosmetic-ingredients/talc",
          sourceQuotes: [
            {
              label: `Talc present · ${product.name || "product"}`,
              source: "Formula screen",
              sourceUrl: "https://www.fda.gov/cosmetics/cosmetic-ingredients/talc",
              quote: `${talcNames.join(", ")} ${talcNames.length === 1 ? "is" : "are"} present in ${product.name || "this product"}. Current records show ${testing.asbestosTestedAt ? `a test dated ${testing.asbestosTestedAt} with result "${testing.result || "(none)"}"` : "no asbestos testing"}.`,
            },
            MOCRA_TALC_QUOTE,
          ],
          highlight: talcNames,
        })
      );
    }
  }

  // ── 8. Fragrance allergen disclosure (aligned with EU Annex III) ─────────
  if (Array.isArray(euAllergenDetails) && euAllergenDetails.length > 0) {
    const disclosed = Array.isArray(mocra.disclosedAllergens) ? mocra.disclosedAllergens : [];
    const disclosedSet = new Set(disclosed.map((a) => normalizeText(a)));
    const undisclosed = euAllergenDetails.filter(
      (a) => !disclosedSet.has(normalizeText(a.inciName))
    );
    if (undisclosed.length > 0) {
      const undisclosedNames = undisclosed.map((a) => a.inciName);
      issues.push(
        createIssue({
          code: "US_MOCRA_FRAGRANCE_ALLERGEN_REVIEW",
          scope: "label",
          severity: "warning",
          blocking: false,
          message: `${undisclosed.length} fragrance allergen${undisclosed.length === 1 ? "" : "s"} pending FDA disclosure rule: ${undisclosedNames.join(", ")}.`,
          source: "FDA proposed rule, 89 FR 53494 (June 25, 2024)",
          sourceUrl: mocraReference.fragranceAllergenDisclosure.sourceUrl,
          sourceQuotes: [
            {
              label: `Undisclosed allergens · ${undisclosed.length}`,
              source: "Formula screen vs EU Annex III",
              sourceUrl: mocraReference.fragranceAllergenDisclosure.sourceUrl,
              quote: `${undisclosedNames.join(", ")} ${undisclosed.length === 1 ? "is" : "are"} recognised as ${undisclosed.length === 1 ? "a fragrance allergen" : "fragrance allergens"} under EU Annex III and would fall within scope of the FDA's proposed fragrance allergen disclosure rule.`,
            },
            MOCRA_FRAGRANCE_ALLERGEN_QUOTE,
          ],
          highlight: undisclosedNames,
        })
      );
    }
  }

  // ── 9. Professional use warning ───────────────────────────────────────────
  if (isProfessionalUseProduct(product)) {
    const hasWarning =
      Boolean(mocra.professionalUseWarningOnLabel) ||
      (product.claims || []).some((claim) =>
        normalizeText(claim).includes("professional use only")
      );
    facts.isProfessionalUse = true;
    if (!hasWarning) {
      issues.push(
        createIssue({
          code: "US_MOCRA_PROFESSIONAL_USE_WARNING_MISSING",
          scope: "label",
          severity: "error",
          blocking: true,
          message: "Professional use product requires label statement identifying professional-use restriction.",
          source: "21 USC § 364e(b)",
          sourceUrl: "https://www.fda.gov/cosmetics/cosmetics-laws-regulations/modernization-cosmetics-regulation-act-2022-mocra",
          sourceQuotes: [
            {
              label: `${product.name || "This product"} · professional use classification`,
              source: "Product metadata",
              sourceUrl: "",
              quote: `${product.name || "This product"} is classified as a professional-use product (function/category: ${[product.function, product.category].filter(Boolean).join(" · ")}) but no professional-use statement is configured on the label draft.`,
            },
            MOCRA_PROFESSIONAL_USE_QUOTE,
          ],
          highlight: [product.function, product.category, ...productHighlight].filter(Boolean),
        })
      );
    }
  }

  // ── 10. GMP compliance attestation ────────────────────────────────────────
  if (!smallBiz) {
    const gmp = mocra.gmp || {};
    if (!gmp.attestationOnFile) {
      issues.push(
        createIssue({
          code: "US_MOCRA_GMP_ATTESTATION_MISSING",
          scope: "manufacturing",
          severity: "warning",
          blocking: false,
          message: "No GMP attestation on file; FDA GMP rule in final rulemaking.",
          source: "MoCRA § 606 / 21 USC § 364b",
          sourceUrl: "https://www.fda.gov/cosmetics/cosmetics-laws-regulations/modernization-cosmetics-regulation-act-2022-mocra",
          sourceQuotes: [
            {
              label: `${product.name || "This product"} · GMP record`,
              source: "Internal manufacturing records",
              sourceUrl: "",
              quote: `No GMP attestation is on file for ${product.name || "this product"}${gmp.lastAuditedAt ? ` (last audit: ${gmp.lastAuditedAt})` : ""}. FDA GMP regulations are in final rulemaking; attestation readiness is expected before effect.`,
            },
            MOCRA_GMP_QUOTE,
          ],
          highlight: productHighlight,
        })
      );
    }
  }

  // ── Status / facts summary ───────────────────────────────────────────────
  const blockingCount = issues.filter((i) => i.blocking).length;
  facts.blockingIssueCount = blockingCount;
  facts.status = blockingCount > 0 ? "Review" : "Clear";

  return { issues, facts };
};

export const detectPfas = detectPfasIngredient;
export const detectTalc = detectTalcIngredient;
export const getMocraReference = () => mocraReference;
